import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from '@/lib/utils';
import { format as formatDateFns } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarIcon,
  Search,
  X,
  Eye,
  Trash2,
  Shield,
  ChevronDown,
  Plus
} from 'lucide-react';
import { saleService } from '@/services/sale.service';
import { clientService } from '@/services/client.service';
import { toast } from 'sonner';
import { GarantiaModal } from '@/components/GarantiaModal';
import { WarrantyModal } from '@/components/WarrantyModal';
import { Sale, CodigoPeca } from '@/pages/MinhasVendas';
import { telegramLogService } from '@/services/telegram-log.service';

export interface Warranty {
  id: number;
  idvenda?: number;
  codigos: string;
  data_compra: string;
  valido_ate: string;
  etapa: string;
  url_download?: string;
  cliente: {
    nome_cliente: string;
    telefone_cliente: string;
    cpf_cliente: string;
  };
  user: {
    id: number;
    codigo: string;
    nome_razao_social: string;
  };
  venda?: any;
}

export default function Garantias() {
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [totalWarranties, setTotalWarranties] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userData, setUserData] = useState<any>(null);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(true);

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filtros
  const [selectBusca, setSelectBusca] = useState<string>('');
  const [termoBusca, setTermoBusca] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modais
  const [warrantyModalOpen, setWarrantyModalOpen] = useState<boolean>(false);
  const [selectedWarranty, setSelectedWarranty] = useState<Warranty | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [warrantyToDelete, setWarrantyToDelete] = useState<Warranty | null>(null);

  // Modal de criação de garantia
  const [garantiaModalOpen, setGarantiaModalOpen] = useState<boolean>(false);
  const [saleForGarantia, setSaleForGarantia] = useState<Sale | null>(null);
  const [garantiaFormData, setGarantiaFormData] = useState({
    cpf_cliente: '',
    telefone_cliente: '',
    nome_cliente: '',
    codigos: [] as CodigoPeca[],
    idcliente: null as number | null,
    sale: {} as any,
    iduser: null as number | null,
    sale_date_created: ''
  });
  const [foundClient, setFoundClient] = useState<boolean>(false);
  const [isSubmittingGarantia, setIsSubmittingGarantia] = useState<boolean>(false);
  
  // Modal de erro ao criar garantia
  const [erroModalOpen, setErroModalOpen] = useState<boolean>(false);
  const [erroMessage, setErroMessage] = useState<string>('');

  // Carrega garantias com paginação server-side
  const loadWarranties = useCallback(async (
    page = currentPage,
    limit = pageSize,
    search = termoBusca,
    tipo = selectBusca,
    start = startDate,
    end = endDate,
    user = userData,
  ) => {
    try {
      const params: any = { page, limit };
      if (search && tipo) {
        params.search = search;
        params.tipo = tipo;
      }
      if (start && end) {
        params.dateStart = format(start, 'yyyy-MM-dd');
        params.dateEnd = format(end, 'yyyy-MM-dd 23:59:59');
      }
      if (user?.role !== 'admin') {
        params.userId = user?.id;
      }

      const response = await saleService.getAllWarrantyPaginated(params);
      if (response && response.data) {
        setWarranties(response.data);
        setTotalWarranties(response.total);
      } else {
        setWarranties([]);
        setTotalWarranties(0);
      }
    } catch (error) {
      console.error('Erro ao carregar garantias:', error);
      toast.error('Erro ao carregar garantias');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, termoBusca, selectBusca, startDate, endDate, userData]);

  useEffect(() => {
    const storedUser = localStorage.getItem('userlogged');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserData(user);
      setIsLoading(true);
      loadWarranties(1, pageSize, '', '', undefined, undefined, user);
    }
  }, []);

  useEffect(() => {
    if (userData) {
      loadWarranties();
    }
  }, [currentPage, pageSize]);

  const totalPages = Math.ceil(totalWarranties / pageSize);

  const aplicarFiltroPorPeriodo = () => {
    if (!startDate || !endDate) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    setCurrentPage(1);
    loadWarranties(1, pageSize, termoBusca, selectBusca, startDate, endDate, userData);
  };

  const triggerSearch = (termo: string, tipo: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (!termo.trim() || !tipo) {
      setCurrentPage(1);
      loadWarranties(1, pageSize, '', '', startDate, endDate, userData);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      loadWarranties(1, pageSize, termo.trim(), tipo, startDate, endDate, userData);
    }, 1500);
  };

  const limparFiltros = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectBusca('');
    setTermoBusca('');
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    setCurrentPage(1);
    loadWarranties(1, pageSize, '', '', undefined, undefined, userData);
  };

  const handleSituationChange = async (warranty: Warranty, newSituation: string) => {
    const oldSituation = warranty.etapa;

    // Atualiza otimisticamente
    const updatedWarranties = warranties.map(w =>
      w.id === warranty.id ? { ...w, etapa: newSituation } : w
    );
    setWarranties(updatedWarranties);

    try {
      await saleService.updateWarrantySituation(warranty.id, newSituation);
      toast.success('Situação atualizada com sucesso!');
      // Recarrega para garantir consistência
      if (userData) {
        loadWarranties();
      }
    } catch (error) {
      // Reverte em caso de erro
      const revertedWarranties = warranties.map(w =>
        w.id === warranty.id ? { ...w, etapa: oldSituation } : w
      );
      setWarranties(revertedWarranties);
      console.error('Erro ao atualizar situação:', error);
      toast.error('Não foi possível atualizar a situação');
    }
  };

  const openWarrantyModal = (warranty: Warranty) => {
    const formattedSale: Sale = {
      id: warranty.idvenda || 0,
      codepagarme: warranty.venda?.codepagarme,
      amount: 0,
      nome_cliente: warranty.cliente.nome_cliente,
      payment_method: '',
      date_created: warranty.data_compra,
      situation: '',
      installments: 1,
      warranty_id: warranty.id,
      userClient: warranty.user,
      warranty: {
        idwarranty: warranty.id,
        url_download: warranty.url_download,
        data_compra: warranty.data_compra,
        client: warranty.cliente,
        codigos: warranty.codigos
      }
    };

    setSelectedWarranty(warranty);
    setWarrantyModalOpen(true);
  };

  const openDeleteModal = (warranty: Warranty) => {
    setWarrantyToDelete(warranty);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!warrantyToDelete) return;

    try {
      await saleService.deleteGarantia({ sale: warrantyToDelete.venda || {} });
      toast.success('Garantia excluída com sucesso!');
      setDeleteModalOpen(false);
      setWarrantyToDelete(null);
      // Recarrega garantias
      if (userData) {
        loadWarranties();
      }
    } catch (error) {
      console.error('Erro ao excluir garantia:', error);
      toast.error('Erro ao excluir garantia');
    }
  };

  const formatDate = (dateString: string): string => {
    return formatDateFns(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  };

  const getSituationBadgeClass = (etapa: string): string => {
    switch (etapa?.toLowerCase()) {
      case 'gerado':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'fabrica':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'credito':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'finalizado':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const searchClient = async (dado: string, valor: string) => {
    try {
      const response = await clientService.getClient(dado, valor);
      
      if (!response.id) {
        toast.info(`Cliente não encontrado, verifique o ${valor === 'telefone_cliente' ? 'telefone' : 'CPF'} ou continue inserindo para criar um novo cliente.`);
        return;
      }

      toast.success('Cliente encontrado! Prossiga com os códigos.');
      setGarantiaFormData(prev => ({
        ...prev,
        idcliente: response.id,
        nome_cliente: response.nome_cliente,
        cpf_cliente: response.cpf_cliente,
        telefone_cliente: response.telefone_cliente.replace(/\D/g, '').replace("55", "").trim()
      }));
      setFoundClient(true);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      toast.error('Erro ao buscar cliente');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando garantias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Garantias</h1>
        <Button onClick={() => setGarantiaModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Garantia
        </Button>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filtros</CardTitle>
            <div className={cn(
              "transition-transform duration-200",
              filtersExpanded ? "rotate-180" : "rotate-0"
            )}>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <div className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          filtersExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}>
          <CardContent className="space-y-4 pb-6">
            {/* Busca por tipo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Buscar por</label>
                <Select value={selectBusca} onValueChange={(value) => {
                  setSelectBusca(value);
                  if (termoBusca.trim()) triggerSearch(termoBusca, value);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cliente">Cliente</SelectItem>
                    <SelectItem value="revendedora">Revendedora</SelectItem>
                    <SelectItem value="codigo">Código do item</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium mb-2 block">Nome, CPF ou Código</label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={selectBusca === 'cliente' ? 'Nome, CPF ou código da cliente...' : selectBusca === 'revendedora' ? 'Nome, CPF ou código da revendedora...' : selectBusca === 'codigo' ? 'Código do produto...' : 'Selecione o tipo de busca...'}
                    value={termoBusca}
                    onChange={(e) => {
                      setTermoBusca(e.target.value);
                      triggerSearch(e.target.value, selectBusca);
                    }}
                    disabled={!selectBusca}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Filtro por período */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-2 block">Data Início</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? formatDateFns(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-2 block">Data Fim</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? formatDateFns(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex gap-2">
                <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate}>
                  Aplicar Período
                </Button>
                <Button onClick={limparFiltros} variant="outline">
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              </div>
            </div>
          </CardContent>
        </div>
      </Card>

      {/* Warranties Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Lista de Garantias ({totalWarranties})</CardTitle>
              <CardDescription>
                Gerenciamento de certificados de garantia de peças
              </CardDescription>
            </div>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50 por pág.</SelectItem>
                <SelectItem value="100">100 por pág.</SelectItem>
                <SelectItem value="150">150 por pág.</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className='px-2'>
          {warranties.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Nenhuma garantia encontrada
              </h3>
              <p className="text-sm text-muted-foreground">
                Tente ajustar os filtros ou crie uma nova garantia.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {warranties.map((warranty) => (
                <Card
                  key={warranty.id}
                  className="transition-all duration-200 hover:shadow-md"
                >
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <Badge
                        variant="outline"
                        className={cn("text-xs", getSituationBadgeClass(warranty.etapa))}
                      >
                        {warranty.etapa === 'gerado' && 'Gerado'}
                        {warranty.etapa === 'fabrica' && 'Na Fábrica'}
                        {warranty.etapa === 'credito' && 'Gerou Créd.'}
                        {warranty.etapa === 'finalizado' && 'Finalizado'}
                        {!['gerado', 'fabrica', 'credito', 'finalizado'].includes(warranty.etapa) && warranty.etapa}
                      </Badge>
                      {warranty.venda && (
                        <span className="text-xs text-muted-foreground">
                          Venda #{warranty.venda.codepagarme || `XDINF${warranty.idvenda}`}
                        </span>
                      )}
                    </div>

                    {/* Client Info */}
                    <div className="mb-3">
                      <div className='flex items-center justify-between'>
                        <div className='w-2/3'>
                          <p className="text-xs text-muted-foreground">Cliente:</p>
                          <p className="text-sm font-medium">{warranty.cliente.nome_cliente}</p>
                          <p className="text-xs text-muted-foreground">{warranty.cliente.cpf_cliente}</p>
                          <p className="text-xs text-muted-foreground">{warranty.cliente.telefone_cliente}</p>
                        </div>
                        <div className='text-right'>
                          {/* Dates */}
                          <div>
                            <p className="text-xs text-muted-foreground">Data Compra:</p>
                            <p className="text-xs font-medium">{formatDate(warranty.data_compra)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Válido até:</p>
                            <p className="text-xs font-medium">{formatDate(warranty.valido_ate)}</p>
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* Revendedor */}
                    {userData?.role === 'admin' && (
                      <div className="mb-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground">Revendedor(a):</p>
                        <p className="text-xs">#{warranty.user.codigo} {warranty.user.nome_razao_social}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 justify-between border-t py-2">
                      {userData?.role === 'admin' && (
                        <Select
                          value={warranty.etapa}
                          onValueChange={(value) => handleSituationChange(warranty, value)}
                        >                          
                          <SelectTrigger className="h-8 text-xs flex-1 mr-2 w-2/3 border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gerado">Gerado</SelectItem>
                            <SelectItem value="fabrica">Na Fábrica</SelectItem>
                            <SelectItem value="credito">Gerou Créd.</SelectItem>
                            <SelectItem value="finalizado">Finalizado</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <div className="flex items-center gap-2 justify-between">
                        <Button
                          variant="outline"
                          size='sm'
                          className="justify-start text-xs"
                          onClick={() => openWarrantyModal(warranty)}  
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Ver {userData?.role !='admin' && 'Garantia'}
                        </Button>
                        {userData?.role === 'admin' && (
                          <Button
                            variant="outline"
                            size='sm'
                            className="text-xs justify-start text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => openDeleteModal(warranty)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </Button>
                        )}
                      </div>

                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-2 px-3">
              <span className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalWarranties)} de {totalWarranties} garantia(s)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Anterior
                </Button>
                <span className="flex items-center text-sm px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warranty Modal */}
      {selectedWarranty && (
        <WarrantyModal
          isOpen={warrantyModalOpen}
          onClose={() => {
            setWarrantyModalOpen(false);
            setSelectedWarranty(null);
          }}
          selectedSale={{
            id: selectedWarranty.idvenda || 0,
            codepagarme: selectedWarranty.venda?.codepagarme,
            amount: 0,
            nome_cliente: selectedWarranty.cliente.nome_cliente,
            payment_method: '',    
            date_created: selectedWarranty.data_compra,
            situation: '',
            installments: 1,
            warranty_id: selectedWarranty.id,
            userClient: selectedWarranty.user,
            warranty: {
              idwarranty: selectedWarranty.id,
              url_download: selectedWarranty.url_download,
              data_compra: selectedWarranty.data_compra,
              client: selectedWarranty.cliente,
              codigos: selectedWarranty.codigos
            }
          }}
          userData={{ nome_razao_social: selectedWarranty.user.nome_razao_social }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p>Tem certeza que deseja excluir esta garantia? Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Erro */}
      <Dialog open={erroModalOpen} onOpenChange={setErroModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              Erro ao Criar Garantia
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">{erroMessage}</p>
          </div>
          <DialogFooter className="flex gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setErroModalOpen(false)}
              className="flex-1"
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                window.location.href = 'https://xdoinfinitto.com.br/painel/garantias';
              }}
              className="flex-1"
            >
              Acessar Página Legada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Criação de Garantia */}
      <GarantiaModal
        isOpen={garantiaModalOpen}
        onClose={() => {
          setGarantiaModalOpen(false);
          setSaleForGarantia(null);
          setGarantiaFormData({
            cpf_cliente: '',
            telefone_cliente: '',
            nome_cliente: '',
            codigos: [],
            idcliente: null,
            sale: {},
            iduser: null,
            sale_date_created: ''
          });
          setFoundClient(false);
        }}
        saleForGarantia={saleForGarantia}
        garantiaFormData={garantiaFormData}
        foundClient={foundClient}
        isSubmittingGarantia={isSubmittingGarantia}
        onCpfChange={(value) => setGarantiaFormData(prev => ({ ...prev, cpf_cliente: value }))}
        onPhoneChange={(value) => setGarantiaFormData(prev => ({ ...prev, telefone_cliente: value }))}
        onNameChange={(value) => setGarantiaFormData(prev => ({ ...prev, nome_cliente: value }))}
        onSearchClient={searchClient}
        onAddCodigo={(codigo, tipo) => {
          const cleanCodigo = codigo.replace(/\D/g, '');
          if (cleanCodigo.length !== 13) {
            toast.error('Código inválido: deve ter 13 dígitos');
            return;
          }
          const novoCodigo: CodigoPeca = {
            id: Date.now(),
            code: `${cleanCodigo} (${tipo})`,
            edit: false
          };
          setGarantiaFormData(prev => ({
            ...prev,
            codigos: [novoCodigo, ...prev.codigos]
          }));
        }}
        onRemoveCodigo={(id) => {
          setGarantiaFormData(prev => ({
            ...prev,
            codigos: prev.codigos.filter(c => c.id !== id)
          }));
        }}
        onSubmitGarantia={async () => {
          if (!garantiaFormData.nome_cliente || garantiaFormData.codigos.length == 0) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
          }

          setIsSubmittingGarantia(true);
          try {
            // Limpa caracteres especiais do telefone antes de enviar
            const telefoneClean = garantiaFormData.telefone_cliente.replace(/\D/g, '').trim();
            
            const garantiaData = {
              ...garantiaFormData,
              telefone_cliente: telefoneClean,
              iduser: userData?.id,
              sale: saleForGarantia || {}
            };

            if(!telefoneClean 
              //|| telefoneClean.length < 11
            ) {
              toast.error('Telefone inválido, verifique e tente novamente');
              setIsSubmittingGarantia(false);
              return;
            }
            if(!garantiaData.cpf_cliente || garantiaData.cpf_cliente.replace(/\D/g, '').length < 11) {
              toast.error('CPF inválido, verifique e tente novamente');
              setIsSubmittingGarantia(false);
              return;
            }

            const response = await saleService.createGarantia(garantiaData);

            if (response && response.success !== false) {
              toast.success('Garantia criada com sucesso!');
              // clear form and close modal
              setGarantiaFormData({
                cpf_cliente: '',
                telefone_cliente: '',
                nome_cliente: '',
                codigos: [],
                idcliente: null,
                sale: {},
                iduser: null,
                sale_date_created: ''
              });
              setGarantiaModalOpen(false);
              // Recarregar garantias
              if (userData) {
                loadWarranties();
              }
            } else {
              console.error('Erro ao criar garantia:', response);
              let msg = '+++ ERRO AO CRIAR GARANTIA +++\n';
              msg += `Revendedora: ${userData.codigo} ${userData.nome_razao_social}\n`;
              msg += '_______________\n';
              msg += '> Dados: \n';
              msg += `<pre>${JSON.stringify({
                ...garantiaFormData,
                iduser: userData?.id,
                sale: saleForGarantia || {}
              }, null, 2)}</pre>\n`;            
              msg += '_______________\n';
              msg += `Resposta: <pre>${JSON.stringify(response, null, 2)}</pre>\n`;
              telegramLogService.send(true, msg, new Date());
              if(response.message && response.message == 'Telefone inválido!') {
                setErroMessage(`Ocorreu um erro, confira o telefone e tente novamente.`);
              }else{
                setErroMessage('Ocorreu um erro ao criar a garantia. Por favor, tente novamente ou acesse a página legada.');
              }
              setErroModalOpen(true);
              setGarantiaModalOpen(false);
            }
          } catch (error) {
            console.error('Erro ao criar garantia:', error);
            let msg = '--- ERRO AO CRIAR GARANTIA ---\n';
            msg += `Revendedora: ${userData.codigo} ${userData.nome_razao_social}\n`;
            msg += '_______________\n';
            msg += '> Dados: \n';
            msg += `<pre>${JSON.stringify({
              ...garantiaFormData,
              iduser: userData?.id,
              sale: saleForGarantia || {}
            }, null, 2)}</pre>\n`;            
            msg += '_______________\n';
            msg += `Erro: <pre>${error}</pre>\n`;
            telegramLogService.send(true, msg, new Date());
            setErroMessage('Ocorreu um erro ao criar a garantia. Por favor, tente novamente ou acesse a página anterior.');
            setErroModalOpen(true);
            setGarantiaModalOpen(false);
          } finally {
            setIsSubmittingGarantia(false);
          }
        }}
      />
    </div>
  );
}